const emptyValue = Symbol('empty');

export class Fork {
  private id!: string;
  private onSuccess: Function[] = [];
  private onError: Function[] = [];
  private onBefore!: Function;
  private onAfter!: Function;
  private isBeforeWasCalled = false;
  private isDebug = false;
  private isDone = false;
  private value: any = emptyValue;
  private isResolved = false;

  constructor() {
    this._before();
  }

  resolve(value: any) {
    this._debug('do resolve');
    if (this.value === emptyValue) {
      this.isResolved = true;
      this.value = value;
    }
    this.done();
  }

  reject(value: any) {
    this._debug('do reject');
    if (this.value === emptyValue) {
      this.isResolved = false;
      this.value = value;
    }
    this.done();
  }

  done() {
    this.isDone = true;
    this._runIfDone();
  }

  success(onSuccess: Function) {
    this._checkDone();
    this._debug('register success');
    this.onSuccess.push(onSuccess);
    return this;
  }

  error(onError: Function) {
    this._checkDone();
    this._debug('register error');
    this.onError.push(onError);
    return this;
  }

  before(onBefore: Function) {
    this._checkDone();
    this._debug('register before');
    this.onBefore = onBefore;

    if (this.isBeforeWasCalled) {
      this._before();
    }

    return this;
  }

  after(onAfter: Function) {
    this._checkDone();
    this._debug('register after');
    this.onAfter = onAfter;
    return this;
  }

  setId(id: string) {
    this._debug('set id');
    this.id = id;
    return this;
  }

  debug() {
    this._checkDone();
    this.isDebug = true;
    return this;
  }

  private _runIfDone() {
    if (this.isDone && this.value !== emptyValue) {
      if (this.isResolved) {
        this.onSuccess.forEach(fn => fn(this.value));
      } else {
        this.onError.forEach(fn => fn(this.value));
      }
      this._after();
    }
  }

  private _checkDone() {
    if (this.isDone) {
      console.warn('Fork is done');
    }
  }

  private _before() {
    this.isBeforeWasCalled = true;
    this.onBefore && this.onBefore();
  }

  private _after() {
    this.onAfter && this.onAfter();
  }

  private _debug(message: string) {
    if (this.isDebug) {
      // eslint-disable-next-line no-console
      console.log(`[${this.id}]`, message);
    }
  }
}

export function forkify(fn: (fork: Fork, args: any[]) => void) {
  return (...args: any[]) => {
    const fork = new Fork();
    Promise.resolve().then(() => {
      fn(fork, args);
    });
    return fork;
  };
}

export function createDecorator(decorator: (fork: Fork, fn: Function, ...args: any[]) => any) {
  return (fn: Function) => (...args: any[]) => {
    const fork = new Fork();
    Promise.resolve().then(() => {
      decorator(fork, fn, ...args);
    });
    return fork;
  };
}
